import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  Input,
  Output,
  QueryList,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { StepperBuilder } from './stepper.types';
import { StepperStepComponent } from './stepper-step/stepper-step.component';
import { Button } from 'primeng/button';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonBlockProps } from '@/components/block-builder/block-builder.types';
import { BehaviorSubject, Observable, of } from 'rxjs';

@Component({
  selector: 'sm-stepper',
  standalone: true,
  imports: [CommonModule, StepperStepComponent, Button, TranslateModule],
  templateUrl: './stepper.component.html',
  styleUrls: ['./stepper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepperComponent implements AfterViewInit {
  @Input() stepperBuilder!: StepperBuilder;

  /** Controlled mode (optional) */
  @Input() activeIndex?: Observable<number>;
  @Output() activeIndexChange = new EventEmitter<number>();

  footerActions: Observable<ButtonBlockProps[]> = of([]);

  /** Internal uncontrolled state fallback */
  private internalActiveIndex$ = new BehaviorSubject<number>(1);

  /** Always use this observable as the source of truth */
  get activeIndex$(): Observable<number> {
    return this.activeIndex ?? this.internalActiveIndex$.asObservable();
  }

  @ViewChildren('dynamicHost', { read: ViewContainerRef })
  hosts!: QueryList<ViewContainerRef>;

  constructor(
    private injector: Injector,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    this.footerActions = this.stepperBuilder.footerActions ?? of([]);
    this.loadActiveStep();
  }

  activateStep(index: number): void {
    if (index < 1 || index > this.stepperBuilder.items.length) return;

    // Controlled mode
    if (this.activeIndex !== undefined) {
      this.activeIndexChange.emit(index);
    }
    // Uncontrolled mode
    else {
      this.internalActiveIndex$.next(index);
    }

    this.stepperBuilder.onActiveIndexChange?.(index);

    this.loadActiveStep();
    this.cdr.markForCheck();
  }

  private loadActiveStep(): void {
    if (!this.hosts?.length) return;

    // We read the current index from the resolved observable
    let current: number;
    this.activeIndex$.subscribe(i => (current = i)).unsubscribe();

    this.hosts.forEach((host, idx) => {
      host.clear();

      const item = this.stepperBuilder.items[idx];
      if (!item?.component) return;

      const ref = host.createComponent(item.component, { injector: this.injector });

      const reserved = ['value', 'label', 'icon', 'disabled', 'component', 'injectKeys'];
      Object.keys(item).forEach(key => {
        if (!reserved.includes(key)) (ref.instance as any)[key] = item[key];
      });

      item.injectKeys?.forEach(k => {
        if (k in item) (ref.instance as any)[k] = item[k];
      });

      ref.changeDetectorRef.detectChanges();
    });
  }
}
